import { MaskReveal, FadeUp } from "./Motion";

/**
 * Reusable editorial page hero — used on all inner pages.
 *
 * `image` is optional. Without one the hero keeps its original full-width text
 * with a watermark chapter numeral on the right.
 *
 * With one, the picture is the section's background rather than a framed panel
 * beside the text, so an inner page reads as part of the same site as the
 * homepage. It is anchored right and its edges are faded back into the page
 * colour, which is what keeps the headline readable over it.
 *
 * `imageFit` picks between two jobs a hero picture can do:
 *
 *   "cover"   (default) — a photograph. It fills the whole right-hand area and
 *                         crops to do it, because nothing is lost by cropping
 *                         a desk or a skyline.
 *
 *   "contain"           — a diagram. Cropping it loses content, and letting it
 *                         run under the text hides content: measured on the
 *                         valuation page, the artwork's icon grid starts at
 *                         622px while the text column reaches 882px, so the
 *                         first two columns of icons sat behind the paragraph.
 *                         So it is scaled to fit, kept clear of the text, and
 *                         its own edges are faded out — no box, no crop, and
 *                         every part of the diagram visible.
 */
export default function PageHero({ eyebrow, lines, blurb, chapter, image, imageAlt, imageFit = "cover" }) {
  const hasImage = Boolean(image);
  const isDiagram = imageFit === "contain";

  // A diagram needs the text column out of its way; a photograph does not.
  const textCols = !hasImage ? "lg:col-span-8" : isDiagram ? "lg:col-span-6" : "lg:col-span-7";

  return (
    <section className="relative overflow-hidden bg-[#060f1e] border-b border-[#1a2940]">
      {hasImage && (
        <div
          className={
            isDiagram
              // Full-bleed atmosphere on a phone; from lg, vertically centred at
              // its own aspect so there is no letterbox band to give away that
              // it is a rectangle sitting on the page.
              ? "absolute inset-y-0 right-0 w-full z-0 lg:inset-y-auto lg:top-1/2 lg:-translate-y-1/2 lg:w-[58%]"
              : "absolute inset-y-0 right-0 w-full lg:w-[68%] z-0"
          }
        >
          <img
            src={image + "-1600.webp"}
            srcSet={image + "-800.webp 800w, " + image + "-1600.webp 1600w"}
            sizes={isDiagram ? "(min-width: 1024px) 58vw, 100vw" : "(min-width: 1024px) 68vw, 100vw"}
            alt={imageAlt || ""}
            className={
              isDiagram
                ? "w-full h-full object-cover object-center lg:h-auto lg:object-contain"
                : "w-full h-full object-cover object-center"
            }
            loading="eager"
            fetchpriority="high"
            width="1600"
            height="924"
          />
          {/*
            Two fades, written as explicit stops rather than Tailwind via-/to-,
            because where the stop sits matters: for a photograph the text column
            ends around 58% of the viewport and the picture starts around 32%, so
            the overlap has to stay near-solid while the right of the frame comes
            through almost clean. An evenly spaced three-stop gradient washes the
            whole picture out, which is the opposite of what was asked for.

            A diagram is already clear of the text, so its left fade only has to
            soften the edge rather than hide anything — clearing by 30% instead
            of 58% keeps every icon legible.
          */}
          <div
            className="absolute inset-0 lg:hidden"
            style={{
              background: isDiagram
                ? "linear-gradient(to right, #060f1e 0%, rgba(6,15,30,0.96) 40%, rgba(6,15,30,0.93) 100%)"
                : "linear-gradient(to right, #060f1e 0%, rgba(6,15,30,0.95) 40%, rgba(6,15,30,0.84) 100%)",
            }}
          />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{
              background: isDiagram
                ? "linear-gradient(to right, #060f1e 0%, rgba(6,15,30,0.82) 6%, rgba(6,15,30,0) 15%)"
                : "linear-gradient(to right, #060f1e 0%, rgba(6,15,30,0.88) 22%, rgba(6,15,30,0.18) 45%, rgba(6,15,30,0) 62%)",
            }}
          />
          <div
            className={isDiagram ? "absolute inset-0 lg:hidden" : "absolute inset-0"}
            style={{ background: "linear-gradient(to bottom, rgba(6,15,30,0.40) 0%, rgba(6,15,30,0) 28%, rgba(6,15,30,0) 72%, rgba(6,15,30,0.70) 100%)" }}
          />
          {isDiagram && (
            <div
              className="absolute inset-0 hidden lg:block"
              // Only enough to dissolve the artwork's own top and bottom edges.
              style={{ background: "linear-gradient(to bottom, #060f1e 0%, rgba(6,15,30,0.55) 7%, rgba(6,15,30,0) 16%, rgba(6,15,30,0) 83%, rgba(6,15,30,0.55) 93%, #060f1e 100%)" }}
            />
          )}
        </div>
      )}

      <div
        className={
          "relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-32 pb-16 lg:pb-28" +
          // Short headlines would otherwise leave the picture as a thin strip.
          (hasImage ? " lg:min-h-[560px] flex flex-col justify-center" : "")
        }
      >
        <div className="grid grid-cols-12 gap-6">
          <div className={"col-span-12 " + textCols}>
            {chapter && (
              <div className="flex items-center gap-4 mb-8">
                <span className="overline text-[#cca24d]">{chapter}</span>
                <span className="h-px w-16 bg-[#cca24d]/40" />
              </div>
            )}
            {eyebrow && (
              <FadeUp className="mb-6" onMount>
                <span className="overline">{eyebrow}</span>
              </FadeUp>
            )}
            <MaskReveal
              lines={lines}
              className={
                hasImage
                  ? "font-serif text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] text-[#f7f5f0]"
                  : "font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.98] text-[#f7f5f0]"
              }
            />
            {blurb && (
              <FadeUp delay={0.4} className="mt-10 max-w-2xl" onMount>
                <p className="text-[#d8d8d8] text-base md:text-lg font-light leading-relaxed">{blurb}</p>
              </FadeUp>
            )}
          </div>

          {/* The watermark numeral and a background picture are alternatives:
              the numeral is decoration for a hero that has none. */}
          {!hasImage && (
            <div className="hidden lg:block col-span-4 relative">
              <div className="absolute right-0 top-1/2 -translate-y-1/2 watermark-num select-none pointer-events-none">
                {chapter?.split(" ")[1] || ""}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
